import React, { useRef, useState, useEffect } from "react";
import IdleTimer, { EVENTS } from "react-idle-timer";
import SessionTimeoutDialog from "./session-timeout-modal";
import _ from 'lodash';
import { cookieDomain } from '../services/cookie-domain';

let countdownInterval: NodeJS.Timeout;
let timeout: NodeJS.Timeout;
const USER_SESSION_TIMEOUT_INTERVAL: number =  _.isFinite(Number(process.env.REACT_APP_USER_SESSION_TIMEOUT_INTERVAL)) ? Number(process.env.REACT_APP_USER_SESSION_TIMEOUT_INTERVAL) : 3600000;

const ACTIVITY_COOKIE_NAME = 'last-activity';
const LOGOUT_COOKIE_NAME = 'logout-event';
const ACTIVITY_EVENTS: EVENTS[] = ['keydown', 'mousedown', 'touchstart', 'wheel'];

const getCookieDomain = () => cookieDomain || window.location.hostname;

/**
 * Cookies to keep track of user activity and logout across other tabs/windows.
 * If user is active in other tabs/windows, we don't want things to timeout and log out.
 * On the other hand, we want to log out if user logs out from other tab
 * */

const writeActivityCookie = _.throttle(() => {
	document.cookie = `${ACTIVITY_COOKIE_NAME}=${Date.now()}; domain=${getCookieDomain()}; path=/;`;
}, 5000, { trailing: false });

const getActivityTimestamp = (): number => {
	const match = document.cookie.match(new RegExp(`${ACTIVITY_COOKIE_NAME}=([^;]+)`));
	return match ? parseInt(match[1], 10) : 0;
};

export const setLogoutCookie = () => {
	document.cookie = `${LOGOUT_COOKIE_NAME}=${Date.now()}; domain=${getCookieDomain()}; path=/;`;
};

const getLogoutTimestamp = (): number => {
	const match = document.cookie.match(new RegExp(`${LOGOUT_COOKIE_NAME}=([^;]+)`));
	return match ? parseInt(match[1], 10) : 0;
};


interface SessionTimeoutProps {
	isAuthenticated: boolean;
	logOut: () => void;
}

const SessionTimeout = (props: SessionTimeoutProps) => {
	const { isAuthenticated, logOut } = props;
	const [timeoutModalOpen, setTimeoutModalOpen] = useState(false);
	const [timeoutCountdown, setTimeoutCountdown] = useState(0);
	const idleTimer = useRef(null);

	useEffect(() => {

		if (!isAuthenticated) return;

		//Kicks off a setInterval that checks whether session has been logged out in other tabs/windows
		const sessionStartTime = Date.now();
		const interval = setInterval(() => {
			const logoutTime = getLogoutTimestamp();
			if (logoutTime > sessionStartTime) {
				logOut();
			}
		}, 5000);

		return () => clearInterval(interval);

	}, [isAuthenticated, logOut]);

	const clearSessionTimeout = () => {
		clearTimeout(timeout);
	};

	const clearSessionInterval = () => {
		clearInterval(countdownInterval);
	};

	const handleLogout = async () => {
		try {
			setTimeoutModalOpen(false);
			clearSessionInterval();
			clearSessionTimeout();
			setLogoutCookie();
			await logOut();
		} catch (err) {
			console.error(err);
		}
	};

	const handleContinue = () => {
		setTimeoutModalOpen(false);
		clearSessionInterval();
		clearSessionTimeout();
		writeActivityCookie.cancel();
		writeActivityCookie();
	};

	const onActive = () => {
		if (!timeoutModalOpen) {
	  	clearSessionInterval();
			clearSessionTimeout();
		}
	};

	const onAction = () => {
		writeActivityCookie();
	};

	const onIdle = () => {
		const delay = 1000 * 1;
		if (isAuthenticated && !timeoutModalOpen) {

			const lastActivity = getActivityTimestamp();
			const timeSinceActivity = Date.now() - lastActivity;

			if (timeSinceActivity < USER_SESSION_TIMEOUT_INTERVAL) {
				(idleTimer.current as any)?.reset();
				return;
			}

			timeout = setTimeout(() => {
				let countDown = 30;
				setTimeoutModalOpen(true);
				setTimeoutCountdown(countDown);
				countdownInterval = setInterval(() => {
					//If other tab/window clicked continue from session timeout modal,
					//we want to close the modal on the other tabs as well
					const lastActivity = getActivityTimestamp();
					if (Date.now() - lastActivity < USER_SESSION_TIMEOUT_INTERVAL) {
						setTimeoutModalOpen(false);
						clearInterval(countdownInterval);
						clearTimeout(timeout);
						(idleTimer.current as any)?.reset();
						return;
					}

					if (countDown > 0) {
						setTimeoutCountdown(--countDown);
					} else {
						clearInterval(countdownInterval);
						handleLogout();
					}
				}, 1000);
			}, delay);
		}
	};

	return (
		<>
			<IdleTimer
				ref={idleTimer}
				onActive={onActive}
				onAction={onAction}
				onIdle={onIdle}
				debounce={250}
				timeout={USER_SESSION_TIMEOUT_INTERVAL}
				events={ACTIVITY_EVENTS}
			/>
			<SessionTimeoutDialog
				countdown={timeoutCountdown}
				onContinue={handleContinue}
				onLogout={() => handleLogout()}
				open={timeoutModalOpen}
			/>
		</>
	);
}
export default SessionTimeout;